import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { articles, categories } from "@/mocks/news";

const BASE_URL = "https://jugxooi.cn";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((item) => item.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="mx-auto flex min-h-[60vh] max-w-content flex-col items-center justify-center px-4 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">内容暂时找不到了</h1>
          <p className="mt-3 text-sm text-foreground-600">这篇文章可能已被移除或链接有误。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((item) => item.slug === article.categorySlug);
  const related = articles
    .filter((item) => item.id !== article.id)
    .sort((a, b) => {
      const sameA = a.categorySlug === article.categorySlug ? 1 : 0;
      const sameB = b.categorySlug === article.categorySlug ? 1 : 0;
      return sameB - sameA || b.heat - a.heat;
    })
    .slice(0, 3);

  const hotList = [...articles].sort((a, b) => b.heat - a.heat).slice(0, 5);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      dateModified: article.date,
      articleSection: article.categoryName,
      keywords: article.tags.join(","),
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: article.author },
      publisher: { "@type": "Organization", name: "美日吃瓜" },
      mainEntityOfPage: `${BASE_URL}/article/${article.id}`,
      url: `${BASE_URL}/article/${article.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${BASE_URL}/` },
        {
          "@type": "ListItem",
          position: 2,
          name: article.categoryName,
          item: `${BASE_URL}/category/${article.categorySlug}`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
          item: `${BASE_URL}/article/${article.id}`,
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${article.title} - 美日吃瓜`}
        description={article.excerpt.slice(0, 150)}
        keywords={`美日吃瓜,${article.categoryName},${article.tags.join(",")}`}
        canonical={`/article/${article.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <Navbar />
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: article.categoryName, to: `/category/${article.categorySlug}` },
          { label: article.title.length > 16 ? `${article.title.slice(0, 16)}…` : article.title },
        ]}
      />
      <main className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <article className="lg:col-span-8">
            <header>
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
              >
                <i className="ri-price-tag-3-line" aria-hidden="true" />
                {article.categoryName}
              </Link>
              <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{article.excerpt}</p>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3 text-xs text-foreground-500 md:text-sm">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line" aria-hidden="true" />
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true" />
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true" />
                  {article.readTime} 分钟阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line" aria-hidden="true" />
                  {article.views}人看过
                </span>
              </div>
            </header>

            <div className="mt-6 h-64 w-full overflow-hidden rounded-lg bg-background-200 md:h-[380px]">
              <img
                src={article.cover}
                alt={`${article.title} 配图`}
                title={`${article.title} - 美日吃瓜`}
                className="h-full w-full object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {article.content.map((paragraph, index) => (
                <p
                  key={`${article.id}-p-${index}`}
                  className="text-[15px] leading-[1.9] text-foreground-700 md:text-base"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {article.timeline && article.timeline.length > 0 ? (
              <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-history-line text-primary-600" aria-hidden="true" />
                  事件时间线
                </h2>
                <ol className="mt-5 space-y-5 border-l border-background-300 pl-5">
                  {article.timeline.map((item) => (
                    <li key={item.time} className="relative">
                      <span className="absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-background-100 bg-accent-500" />
                      <span className="block text-xs font-bold tracking-wide text-primary-700">
                        {item.time}
                      </span>
                      <span className="mt-1 block text-sm leading-relaxed text-foreground-700">
                        {item.text}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            ) : null}

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">相关标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          <aside className="lg:col-span-4" aria-label="相关推荐">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">频道入口</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {category ? category.description : ""}
              </p>
              <Link
                to={`/category/${article.categorySlug}`}
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                浏览「{article.categoryName}」全部内容
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 border-b border-background-300 pb-4 font-heading text-base font-bold text-foreground-950">
                <i className="ri-fire-fill text-primary-500" aria-hidden="true" />
                全站热榜
              </h2>
              <ol className="mt-2 divide-y divide-background-300">
                {hotList.map((item, index) => (
                  <li key={item.id}>
                    <Link
                      to={`/article/${item.id}`}
                      title={`${item.title} - 美日吃瓜`}
                      className="group flex items-start gap-3 py-3"
                    >
                      <span
                        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                          index < 3
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-300 text-foreground-700"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="block text-sm font-medium leading-relaxed text-foreground-800 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>

        <section className="mt-14 border-t border-background-200 pt-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950">你可能还想看</h2>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <ArticleCard key={item.id} article={item} showExcerpt={false} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}